"use client";

import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { ActiveNotificationIcon } from "@/components/appIcons";
import { AlertDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";
import { client } from "@/utils/orpc.client";

interface AdminOfferEmitButtonProps {
  offerId: string;
  offerTitle: string;
  providerName: string;
  className?: string;
}

/** Admin-only: shares an offer as a best deal through matching personal and public channels. */
export function AdminOfferEmitButton({
  offerId,
  offerTitle,
  providerName,
  className,
}: AdminOfferEmitButtonProps) {
  const t = useTranslations("globals.common");
  const { data: session } = useSession();
  const mutation = useMutation({
    mutationFn: () => client.deals.adminShareOfferBestDeal({ offerId }),
    onSuccess: (result) => {
      if (result.emitted) toast.success(t("offerEmitted"));
      else toast.error(t("offerEmitSkipped", { reason: result.reason }));
    },
    onError: () => {
      toast.error(t("offerEmitFailed"));
    },
  });

  if (session?.user.role !== "admin") return null;

  const label = mutation.isPending ? t("emittingOffer") : t("emitOffer");

  return (
    <AlertDialog.Root>
      <AlertDialog.Trigger
        render={
          <Button
            variant="outline"
            size="icon"
            disabled={mutation.isPending}
            aria-busy={mutation.isPending}
            aria-label={label}
            title={label}
            className={cn(
              "size-11 shrink-0 border-blue-600 bg-blue-600 text-white hover:bg-blue-700 hover:text-white",
              className,
            )}
          >
            <ActiveNotificationIcon className="text-[20px]" />
          </Button>
        }
        onClick={(event) => {
          event.stopPropagation();
        }}
      />
      <AlertDialog.Portal>
        <AlertDialog.Backdrop />
        <AlertDialog.Popup>
          <AlertDialog.Title className="text-lg font-bold">{t("emitOfferTitle")}</AlertDialog.Title>
          <AlertDialog.Description className="mt-2 text-sm text-muted-foreground">
            {t("emitOfferDescription", { offer: offerTitle, store: providerName })}
          </AlertDialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <AlertDialog.Close
              data-autofocus
              className="rounded-lg px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary"
            >
              {t("emitOfferCancel")}
            </AlertDialog.Close>
            <AlertDialog.Close
              onClick={() => mutation.mutate()}
              className="rounded-lg bg-primary/10 px-4 py-2 text-sm font-bold text-primary transition-colors hover:bg-primary/20"
            >
              {t("emitOfferConfirm")}
            </AlertDialog.Close>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
